import React, { useState, useEffect } from "react";
import ContactFormModal from "../../../GlobalComponents/ContactFormModal"; // adjust path if needed

const CaseStudies = () => {
  const [selectedCase, setSelectedCase] = useState(null);
  const [showContactForm, setShowContactForm] = useState(false);

  const caseStudies = [
    {
      id: 1,
      title: "Hotel Chain Pest Management",
      client: "Luxury Hotels Group",
      challenge: "Rodent infestation affecting 5-star ratings",
      solution: "Integrated pest management system",
      results: "100% pest-free for 12+ months",
      image: "https://images.unsplash.com/photo-1566073771259-6a8506099945?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      fullDescription: "A comprehensive case study detailing how we deployed over 100 monitoring stations, sealed entry points, and trained staff to maintain a rodent-free environment across three luxury hotels. Our integrated approach combined baiting, exclusion, and sanitation protocols to achieve sustained results."
    },
    {
      id: 2,
      title: "Agricultural Pest Control",
      client: "Green Valley Farms",
      challenge: "Crop destruction by locusts",
      solution: "Aerial spraying and monitoring",
      results: "85% crop preservation",
      image: "https://images.unsplash.com/photo-1500382017468-9049fed747ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      fullDescription: "Working with a 500‑hectare farm, we coordinated aerial spraying at dawn to target locust swarms without harming beneficial insects. We also implemented ground monitoring and early‑warning systems to prevent future outbreaks, saving the majority of the season's yield."
    },
    {
      id: 3,
      title: "Residential Termite Elimination",
      client: "Private Homeowner",
      challenge: "Structural damage from termites",
      solution: "Complete termite barrier system",
      results: "Property value preserved",
      image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      fullDescription: "After discovering extensive subterranean termite damage, we installed a chemical barrier around the foundation, treated affected wood, and set up monitoring stations. The homeowner avoided costly repairs and now enjoys a 5‑year warranty."
    }
  ];

  const openModal = (caseStudy) => setSelectedCase(caseStudy);
  const closeModal = () => setSelectedCase(null);

  // Close modal on Escape key
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, []);

  const handleConsultationClick = () => {
    setShowContactForm(true);
  };

  return (
    <section className="case-studies">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Featured Case Studies</h2>
          <p className="section-subtitle">
            In-depth look at how we solved complex pest control challenges
          </p>
        </div>
        
        <div className="case-studies-grid">
          {caseStudies.map((study) => (
            <div key={study.id} className="case-study-card">
              <div className="case-study-image">
                <img src={study.image} alt={study.title} />
              </div>
              
              <div className="case-study-content">
                <h3 className="case-study-title">{study.title}</h3>
                <div className="case-study-client">Client: {study.client}</div>
                
                <div className="case-study-details">
                  <div className="detail-section">
                    <h4>Challenge</h4>
                    <p>{study.challenge}</p>
                  </div>
                  
                  <div className="detail-section">
                    <h4>Our Solution</h4>
                    <p>{study.solution}</p>
                  </div>
                  
                  <div className="detail-section">
                    <h4>Results</h4>
                    <p className="results-highlight">{study.results}</p>
                  </div>
                </div>
                
                <button 
                  className="read-full-study"
                  onClick={() => openModal(study)}
                >
                  Read Full Case Study
                </button>
              </div>
            </div>
          ))}
        </div>
        
        <div className="case-studies-cta">
          <h3>Need a Custom Solution?</h3>
          <p>Let's discuss your specific pest control challenges</p>
          <button className="cta-button" onClick={handleConsultationClick}>
            Get Free Consultation
          </button>
        </div>
      </div>

      {/* Modal for full case study */}
      {selectedCase && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>×</button>
            <h3 className="modal-title">{selectedCase.title}</h3>
            <p className="modal-client"><strong>Client:</strong> {selectedCase.client}</p>
            
            <div className="modal-image-container">
              <img src={selectedCase.image} alt={selectedCase.title} className="modal-image" />
            </div>

            <div className="modal-sections">
              <div className="modal-section">
                <h4>Challenge</h4>
                <p>{selectedCase.challenge}</p>
              </div>
              <div className="modal-section">
                <h4>Our Solution</h4>
                <p>{selectedCase.solution}</p>
              </div>
              <div className="modal-section">
                <h4>Results</h4>
                <p className="results-highlight">{selectedCase.results}</p>
              </div>
              <div className="modal-section full-description">
                <h4>Full Case Study</h4>
                <p>{selectedCase.fullDescription}</p>
              </div>
            </div>

            <button className="modal-close-btn" onClick={closeModal}>
              Close
            </button>
          </div>
        </div>
      )}

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={showContactForm}
        onClose={() => setShowContactForm(false)}
      />

      <style jsx>{`
        .case-studies {
          padding: 4rem 2rem;
          background: #f8f9fa;
        }
        
        .container {
          max-width: 1200px;
          margin: 0 auto;
        }
        
        .section-header {
          text-align: center;
          margin-bottom: 3rem;
        }
        
        .section-title {
          font-size: 2.5rem;
          font-weight: bold;
          color: #2c3e50;
          margin: 0 0 1rem 0;
        }
        
        .section-subtitle {
          font-size: 1.1rem;
          color: #6c757d;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
        }
        
        .case-studies-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
          gap: 2rem;
          margin-bottom: 3rem;
        }
        
        .case-study-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
        }
        
        .case-study-card:hover {
          transform: translateY(-5px);
        }
        
        .case-study-image {
          height: 200px;
          overflow: hidden;
        }
        
        .case-study-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .case-study-content {
          padding: 1.5rem;
        }
        
        .case-study-title {
          font-size: 1.3rem;
          font-weight: bold;
          color: #2c3e50;
          margin: 0 0 0.5rem 0;
        }
        
        .case-study-client {
          color: #3498db;
          font-weight: 600;
          margin-bottom: 1.5rem;
          font-size: 0.9rem;
        }
        
        .case-study-details {
          margin: 1.5rem 0;
        }
        
        .detail-section {
          margin-bottom: 1.5rem;
        }
        
        .detail-section h4 {
          font-size: 1rem;
          color: #2c3e50;
          margin: 0 0 0.5rem 0;
          font-weight: 600;
        }
        
        .detail-section p {
          color: #6c757d;
          line-height: 1.6;
          margin: 0;
          font-size: 0.95rem;
        }
        
        .results-highlight {
          color: #27ae60 !important;
          font-weight: 600;
        }
        
        .read-full-study {
          width: 100%;
          background: #3498db;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          transition: background 0.3s ease;
        }
        
        .read-full-study:hover {
          background: #2980b9;
        }
        
        .case-studies-cta {
          text-align: center;
          background: linear-gradient(135deg, #1a2980 0%, #26d0ce 100%);
          padding: 2rem;
          border-radius: 12px;
          box-shadow: 0 8px 25px rgba(26, 41, 128, 0.3);
          color: white;
        }
        
        .case-studies-cta h3 {
          font-size: 1.8rem;
          color: white;
          margin: 0 0 1rem 0;
        }
        
        .case-studies-cta p {
          color: rgba(255, 255, 255, 0.9);
          margin: 0 0 2rem 0;
        }
        
        .cta-button {
          background: #27ae60;
          color: white;
          border: none;
          padding: 12px 30px;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.3s ease;
          box-shadow: 0 5px 18px rgba(39, 174, 96, 0.4);
        }
        
        .cta-button:hover {
          background: #219653;
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(39, 174, 96, 0.5);
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          backdrop-filter: blur(5px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 1rem;
        }

        .modal-content {
          background: white;
          border-radius: 16px;
          max-width: 700px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          padding: 2rem;
          position: relative;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        .modal-close {
          position: absolute;
          top: 1rem;
          right: 1rem;
          background: none;
          border: none;
          font-size: 2rem;
          line-height: 1;
          cursor: pointer;
          color: #6c757d;
          padding: 0.25rem 0.75rem;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .modal-close:hover {
          background: #f1f3f5;
          color: #2c3e50;
        }

        .modal-title {
          font-size: 1.8rem;
          font-weight: bold;
          color: #2c3e50;
          margin: 0 0 0.5rem 0;
          padding-right: 2rem;
        }

        .modal-client {
          color: #3498db;
          font-weight: 600;
          margin-bottom: 1.5rem;
        }

        .modal-image-container {
          width: 100%;
          height: 250px;
          overflow: hidden;
          border-radius: 8px;
          margin-bottom: 1.5rem;
        }

        .modal-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .modal-sections {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .modal-section h4 {
          font-size: 1.1rem;
          color: #2c3e50;
          margin: 0 0 0.3rem 0;
          font-weight: 600;
        }

        .modal-section p {
          color: #495057;
          line-height: 1.6;
          margin: 0;
        }

        .full-description {
          margin-top: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #e9ecef;
        }

        .modal-close-btn {
          background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          width: 100%;
          margin-top: 2rem;
        }

        .modal-close-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(108, 117, 125, 0.4);
        }

        @media (max-width: 768px) {
          .case-studies {
            padding: 3rem 1rem;
          }
          
          .section-title {
            font-size: 2rem;
          }
          
          .case-studies-grid {
            grid-template-columns: 1fr;
          }

          .modal-content {
            padding: 1.5rem;
          }

          .modal-title {
            font-size: 1.5rem;
          }

          .modal-image-container {
            height: 200px;
          }
        }

        @media (max-width: 480px) {
          .modal-content {
            padding: 1.2rem;
          }

          .modal-title {
            font-size: 1.3rem;
          }

          .modal-image-container {
            height: 180px;
          }
        }
      `}</style>
    </section>
  );
};

export default CaseStudies;